import { useContext, forwardRef } from 'react';
import PropTypes from 'prop-types';
import i18nContext from 'components/i18n-context';

const KEY_FIELDS = [
  { label: 'Sender Wallet', index: 0 },
  { label: 'Amount', index: 1 },
  { label: 'Currency', index: 2 },
  { label: 'Beneficiary', index: null },
  { label: 'Account Number', index: 5 }
];

const getBeneficiaryName = (rowData) => {
  if (!rowData) return '—';
  const type = (rowData[11] || '').toUpperCase();
  if (type === 'CORPORATE') {
    return rowData[14] || '—';
  }
  const first = rowData[12] || '';
  const last = rowData[13] || '';
  return (first + ' ' + last).trim() || '—';
};

const CsvValidationErrors = forwardRef(({ errors, csvData }, ref) => {
  const i18n = useContext(i18nContext);

  if (!errors || errors.length === 0) return null;

  const totalErrorRows = errors.length;

  const getRowData = (rowNumber) => {
    if (!csvData || !csvData.rows) return null;
    return csvData.rows[rowNumber - 1] || null;
  };

  return (
    <div className={'csv-errors'} ref={ref}>
      <div className={'csv-errors-header'}>
        <p className={'csv-errors-header-title'}>{i18n.getMessage('csvImport.validationErrors.title')}</p>
        <p className={'csv-errors-header-summary'}>
          {i18n.getMessage('csvImport.validationErrors.summary', { count: totalErrorRows })}
        </p>
        <p className={'csv-errors-header-notice'}>{i18n.getMessage('csvImport.validationErrors.notice')}</p>
      </div>

      {errors.map((rowError, index) => {
        const rowData = getRowData(rowError.row);

        return (
          <div key={index} className={'csv-errors-card'}>
            <div className={'csv-errors-card-label'}>
              {i18n.getMessage('csvImport.validationErrors.row', { row: rowError.row })}
            </div>

            {rowData && (
              <table className={'csv-errors-card-table'}>
                <thead>
                  <tr>
                    {KEY_FIELDS.map((field) => (
                      <th key={field.label}>{field.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    {KEY_FIELDS.map((field) => (
                      <td key={field.label}>
                        {field.label === 'Beneficiary' ? getBeneficiaryName(rowData) : rowData[field.index] || '—'}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            )}

            <ul className={'csv-errors-card-messages'}>
              {rowError.fields.map((fieldError, fieldIndex) => (
                <li key={fieldIndex} className={'csv-errors-card-message'}>
                  {fieldError.field ? (
                    <>
                      <strong>{fieldError.field}:</strong> {fieldError.message}
                    </>
                  ) : (
                    fieldError.message
                  )}
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
});

CsvValidationErrors.displayName = 'CsvValidationErrors';

CsvValidationErrors.propTypes = {
  errors: PropTypes.arrayOf(
    PropTypes.shape({
      row: PropTypes.number.isRequired,
      fields: PropTypes.arrayOf(
        PropTypes.shape({
          field: PropTypes.string,
          message: PropTypes.string.isRequired
        })
      ).isRequired
    })
  ),
  csvData: PropTypes.shape({
    headers: PropTypes.arrayOf(PropTypes.string),
    rows: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.string))
  })
};

export default CsvValidationErrors;
